import {
  getModel,
  getViewer,
  utils as macheteReduxUtils,
} from 'src/machete-redux';
import constants from 'src/constants';
import macheteUtils from 'src/utils/macheteUtils';
import theme from 'src/theme';
import createDebug from 'debug';
import { Box3, Group, Vector3 } from '@hover/machete-primitives';
import MacheteCore from '@hover/machete-core';

const debug = createDebug('designPro.split.utils');
const error = createDebug('designPro.saga.error');

const { SPLIT_MODES } = constants.app;
const { getSplitProperties } = macheteUtils;

const splitIndicatorGroup = new Group();
const boundingBox = new Box3();

// only add indicator group once
let splitIndicatorAdded = false;
function addSplitIndicatorGroup() {
  if (splitIndicatorAdded) return;

  const viewer = getViewer();
  viewer.layers.get('helperLayer').add(splitIndicatorGroup);

  splitIndicatorAdded = true;
}

function clearSplitIndicatorLine() {
  while (splitIndicatorGroup.children.length > 0) {
    const child = splitIndicatorGroup.children[0];
    child.parent.remove(child);
  }
  splitIndicatorGroup.setNeedsUpdate(true);
}

/*
 * @param {Array<Object>} lines
 */
const drawIndicatorLines = (lines) => {
  lines.forEach((line) => {
    const edgeHelper = new MacheteCore.Machete.EdgeHelper([line], {
      includePoints: true,
      color: theme.colors.red,
    });
    splitIndicatorGroup.add(edgeHelper.entity);
    edgeHelper.entity.setNeedsUpdate(true);
  });
};

/*
 * @param {Object} node - Hover.SegmentationNode
 * @param {function(node: Object)} cb - the callback function
 */
function traverseChildren(node, cb) {
  cb(node);
  node.children.forEach((child) => {
    traverseChildren(child, cb);
  });
}

/*
 * @param {string} uuid
 */
function getAllSplitDescendants(uuid) {
  const children = [];
  const node = macheteReduxUtils.getNode(uuid);
  const parentOrientation = node.getProperty('orientation') || {};

  traverseChildren(node, (currentNode) => {
    if (currentNode.uuid === uuid) return;
    const { type } = currentNode;
    const present = currentNode.getProperty('present') || {};
    const orientation = currentNode.getProperty('orientation') || {};
    const isSameOrientation = parentOrientation.value === orientation.value;
    if (present.value && type === 'split' && isSameOrientation) {
      children.push(currentNode);
    }
  });

  return children;
}

/*
 * @param {string} uuid
 */
function getSplitChildUUID(uuid) {
  const { children = [] } = macheteReduxUtils.getNode(uuid);
  const child = children.find(({ type }) => type === 'split') || {};
  debug(`found split child for parent uuid ${uuid}: ${child.uuid}`);
  return child.uuid;
}

/**
 * Returns all the currentModelElements for the machete nodes that are
 * selected to be split, used to determine dimensions. When editing, we need
 * to include the child split elements, since the split range should
 * include both the parent and split child dimensions.
 * @param {Array<string>} uuids
 * @param {boolean} isEditingSplit
 */
function getSplitRangeModelElements(uuids, isEditingSplit) {
  const splitRangeElements = [];

  uuids.forEach((uuid) => {
    const node = macheteReduxUtils.getNode(uuid);
    const { currentModelElements = [] } = node;
    currentModelElements.forEach((el) => splitRangeElements.push(el));

    if (isEditingSplit) {
      traverseChildren(node, (currentNode) => {
        if (currentNode.uuid === uuid) return;
        const present = currentNode.getProperty('present') || {};
        if (currentNode.type === 'split' && present.value === true) {
          const { currentModelElements: childEls = [] } = currentNode;
          childEls.forEach((el) => splitRangeElements.push(el));
        }
      });
    }
  });

  return splitRangeElements;
}

/**
 * Returns min/max height for a set of points
 * @param {Object} box - Box3
 */
function getHeightRangeFromBoundingBox(box) {
  const maxPointCopy = new Vector3();
  const minPointCopy = new Vector3();

  maxPointCopy.copy(box.max);
  minPointCopy.copy(box.min);

  maxPointCopy.multiply(MacheteCore.Machete.upVector);
  minPointCopy.multiply(MacheteCore.Machete.upVector);

  const maxHeight = box.max.dot(MacheteCore.Machete.upVector);
  const minHeight = box.min.dot(MacheteCore.Machete.upVector);

  return {
    maxHeight,
    minHeight,
  };
}

/**
 * @param {string} nodeUUID
 */
function getHeightRangeForNodeElements(nodeUUID) {
  boundingBox.makeEmpty();

  const node = macheteReduxUtils.getNode(nodeUUID);
  const { modelElements = [] } = node;
  modelElements.forEach((element) => {
    element.outerLoop.forEach((point) => {
      boundingBox.expandByPoint(point);
    });
  });

  return getHeightRangeFromBoundingBox(boundingBox);
}

/**
 * @param {Object} element - Hover.ModelElement
 */
function getHeightRangeForElement(element) {
  boundingBox.makeEmpty();

  element.outerLoop.forEach((point) => boundingBox.expandByPoint(point));

  return getHeightRangeFromBoundingBox(boundingBox);
}

/**
 * Gets parent model elements and draws the split indicator line on them
 * if they fall within the height that the line exists
 */
function drawIndicator(offset, orientation, uuids, isEditingSplit) {
  const splitRangeElements = getSplitRangeModelElements(uuids, isEditingSplit);
  const referencePoint = new Vector3();
  const {
    getFacadeSplitFromPointAndOrientation,
  } = MacheteCore.Machete.SegmentationUtils;
  const { getBoundingRect } = MacheteCore.Machete.PolygonUtils;

  if (orientation === SPLIT_MODES.HORIZONTAL) {
    splitRangeElements.forEach((el) => {
      const { maxHeight, minHeight } = getHeightRangeForElement(el);

      const elementExistAtSplitOffset =
        offset < maxHeight && offset > minHeight;
      if (elementExistAtSplitOffset) {
        const heightOffset = offset - minHeight;
        referencePoint.copy(boundingBox.min).addScalar(heightOffset);
        const split = getFacadeSplitFromPointAndOrientation(
          el,
          referencePoint,
          orientation,
        );
        drawIndicatorLines(split.lines);
      }
    });
  } else {
    const outerLoopPoints = splitRangeElements.reduce((acc, element) => {
      element.outerLoop.forEach((point) => acc.push(point));
      return acc;
    }, []);
    const rect = getBoundingRect(outerLoopPoints, splitRangeElements[0].normal);
    splitRangeElements.forEach((el) => {
      const direction = new Vector3();
      const offsetPoint = new Vector3();
      direction
        .copy(rect.bottomRight)
        .sub(rect.bottomLeft)
        .normalize();
      offsetPoint.copy(direction).multiplyScalar(offset);
      referencePoint.copy(rect.bottomLeft).add(offsetPoint);
      const split = getFacadeSplitFromPointAndOrientation(
        el,
        referencePoint,
        orientation,
      );
      drawIndicatorLines(split.lines);
    });
  }
}

/**
 * Deletes a node from the machete segmentation tree
 */
function deleteMacheteNode(uuid) {
  const nodeToDelete = macheteReduxUtils.getNode(uuid);
  const replacementNodeUUID = splitUtils.getSplitChildUUID(uuid);
  const replacementNode = macheteReduxUtils.getNode(replacementNodeUUID);
  const parentNode = nodeToDelete.parent;
  const tree = getModel().segmentationTree;
  tree.addNodeTo(replacementNode, parentNode);
  tree.removeNode(nodeToDelete);
  tree.updateAll();
}

/**
 * Determines min/max slider values for the split control. If horizontal
 * split, expands a bounding box with all the selected elements outer loops to
 * determine the max and min height that could be split. If vertical, the
 * range is 0 through the width of the element.
 */
function getSplitRange(uuids, orientation, isEditingSplit) {
  const elements = getSplitRangeModelElements(uuids, isEditingSplit);

  // horizontal split range
  if (orientation === SPLIT_MODES.HORIZONTAL) {
    boundingBox.makeEmpty();
    elements.forEach((el) =>
      el.outerLoop.forEach((point) => boundingBox.expandByPoint(point)),
    );
    const {
      maxHeight: maxOffset,
      minHeight: minOffset,
    } = getHeightRangeFromBoundingBox(boundingBox);
    return { minOffset, maxOffset };
  }

  // vertical split range
  const minOffset = 0;
  const outerLoopPoints = elements.reduce((acc, element) => {
    element.outerLoop.forEach((point) => acc.push(point));
    return acc;
  }, []);
  const { getBoundingRect } = MacheteCore.Machete.PolygonUtils;
  const rect = getBoundingRect(outerLoopPoints, elements[0].normal);
  const maxOffset = rect.width;
  return { minOffset, maxOffset };
}

/**
 * Deletes any child split elements that overlap the offset line or are above it
 */
function deleteOverlappingChildSplits(splitUUID, offset) {
  const splitDescendants = getAllSplitDescendants(splitUUID);

  splitDescendants.reverse().forEach(({ uuid }) => {
    const { minHeight } = getHeightRangeForNodeElements(uuid);
    if (offset >= minHeight) {
      debug(`deleting ${uuid} since ${splitUUID} now overlaps it`);
      deleteMacheteNode(uuid);
    }
  });
}

/**
 * @param {string} parentUUID
 * @returns {Array<Object>} Returns an array of objects with it's parent's
 * uuid and it's split properties (size, orientation, present) for all
 * the split descendants of the uuid passed as the parentUUID param
 */
function getSplitChildrenPropertiesArray(parentUUID) {
  const childSplitNodeData = [];
  const splitUUID = getSplitChildUUID(parentUUID);
  let parent = macheteReduxUtils.getNode(parentUUID);
  let child = splitUUID ? macheteReduxUtils.getNode(splitUUID) : null;

  while (child) {
    const splitProperties = getSplitProperties(child);
    childSplitNodeData.push({
      uuid: parent.uuid,
      ...splitProperties,
    });

    parent = child;
    child = parent.children.find(({ type }) => type === 'split');
  }

  return childSplitNodeData;
}

function getOrientationProperty(parentUUID) {
  const splitUUID = getSplitChildUUID(parentUUID);
  const node = macheteReduxUtils.getNode(splitUUID);
  const orientationProperty = node.getProperty('orientation') || {};

  return orientationProperty.value;
}

const splitUtils = {
  boundingBox,
  addSplitIndicatorGroup,
  clearSplitIndicatorLine,
  deleteMacheteNode,
  deleteOverlappingChildSplits,
  drawIndicator,
  drawIndicatorLines,
  getAllSplitDescendants,
  getHeightRangeForNodeElements,
  getHeightRangeFromBoundingBox,
  getHeightRangeForElement,
  getOrientationProperty,
  getSplitChildrenPropertiesArray,
  getSplitChildUUID,
  getSplitRange,
  getSplitRangeModelElements,
  traverseChildren,
};

export default splitUtils;
